import { Alert, Button, Form, Input, Segmented, Tag, Tooltip } from 'antd';
import { Trash2 } from 'lucide-react';
import type { PolicyListItemDto } from '@pams/shared';
import { nameOr, withChosenOption } from '@/lib/selectOptions';
import { CoverageBar } from '@/components/intake/CoverageBar';
import { TimeField } from '@/components/intake/TimeField';
import { DatePicker } from '@/components/DatePicker';
import type { ArrangementDraft } from '@/components/intake/draft';
import { RTC_COPY } from '../copy';
import type { StandingOrderEndsKind } from '../formValues';

/**
 * The two sections a standing order does NOT share with the PA intake, and one it could not.
 *
 * ⚠️ EVERY OTHER SECTION ON THIS FORM IS THE PA INTAKE'S OWN COMPONENT, imported from
 * `components/intake/` and rendered unchanged — Places, the force grid and Instructions.
 * That reuse is the whole test of whether the section registry did its job: if a section
 * had to be COPIED to make round-the-clock work, the registry would not be doing anything.
 *
 * What is here is what genuinely differs:
 *
 *   BASICS   a standing order has no ORDER DATE, so it cannot reuse the intake's Basics.
 *   SHIFTS   a relief pattern with a 24-hour coverage bar under it, and NO
 *            "no fixed time / single / multiple" segment — a round-the-clock order is
 *            shifts by definition, and offering "no fixed time" would make the bar
 *            meaningless.
 *   WHEN     a start date and one of two endings. ⚠️ NOT P3b.0's `AFTER_N`: "ten repeats"
 *            is meaningless for something whose whole point is that it keeps going.
 *
 * ⚠️ THERE IS NO COMMAND SECTION ANYWHERE ON THIS FORM. Not hidden, not empty — absent
 * from the registry, so its fields are never registered at all.
 */

type SectionProps = { draft: ArrangementDraft; readOnly: boolean };

export const StandingOrderBasics = ({
  draft,
  readOnly,
  policies,
  resolvePolicy,
}: SectionProps & {
  policies: readonly Pick<PolicyListItemDto, 'id' | 'name' | 'isDefault'>[];
  /** ⚠️ [D4.5 §1b] What an already-stored `policyId` renders as — archived ones included. */
  resolvePolicy?: (id: string) => { name: string } | undefined;
}) => (
  <div className="flex flex-col gap-3">
    {policies.length > 1 ? (
      <Form.Item name="policyId" label="Policy" className="mb-0" data-testid="policy-chips">
        {/*
          ⚠️ [D4.5 §1b] A `Segmented` FAILS SILENTLY WHERE A `Select` FAILS LOUDLY.
          A value with no matching option highlights NOTHING — no id on screen, but also no
          selection, so an order whose policy was archived reads as though nobody had chosen
          one. Same root cause as the audit's eight `Select`s, opposite symptom, and the same
          remedy: the stored value always has an option (`lib/selectOptions.ts`).
        */}
        <Segmented
          disabled={readOnly}
          options={withChosenOption(
            policies.map((policy) => ({ value: policy.id, label: policy.name })),
            draft.policyId || null,
            resolvePolicy,
          )}
        />
      </Form.Item>
    ) : (
      <>
        {/* One option is not a choice — it is a click and a decision to read. State it. */}
        <p className="text-sm text-muted-foreground" data-testid="policy-single">
          Policy:{' '}
          <span className="text-foreground">{policies[0]?.name ?? nameOr(resolvePolicy?.(draft.policyId))}</span>
        </p>
        <Form.Item name="policyId" hidden>
          <input type="hidden" />
        </Form.Item>
      </>
    )}

    <Form.Item name="title" label="Name" tooltip={RTC_COPY.title} className="mb-0" required>
      <Input placeholder="Park Street naka" disabled={readOnly} data-testid="rtc-title" />
    </Form.Item>
    <span className="hidden">{draft.title}</span>
  </div>
);

export const ShiftPatternSection = ({ draft, readOnly }: SectionProps) => (
  <div className="flex flex-col gap-3">
    <p className="text-xs text-muted-foreground">{RTC_COPY.shiftsSeeded}</p>

    <Form.List name="shifts">
      {(fields, { remove }) => (
        <div className="flex flex-col gap-2">
          {fields.map((field, index) => {
            const shift = draft.shifts[index];
            /* ⚠️ THE SAME `<=` RULE AS `slot.util.ts` AND THE COVERAGE BAR. One comparison,
               one meaning, and the existing badge rather than a second one. */
            const overnight =
              shift !== undefined && shift.startTime !== '' && shift.endTime !== '' && shift.endTime <= shift.startTime;

            return (
              /* ⚠️ [P3b.2] NO LABEL ROW — see the PA intake's shift row for the whole of why. */
              <div
                key={field.key}
                className="flex flex-wrap items-start gap-2"
                data-testid={`rtc-shift-${String(index)}`}
              >
                <Form.Item name={[field.name, 'startTime']} className="mb-0">
                  <TimeField
                    placeholder="From"
                    className="w-28"
                    disabled={readOnly}
                    aria-label={`Shift ${String(index + 1)} start`}
                  />
                </Form.Item>
                <Form.Item name={[field.name, 'endTime']} className="mb-0">
                  <TimeField
                    placeholder="To"
                    className="w-28"
                    disabled={readOnly}
                    aria-label={`Shift ${String(index + 1)} end`}
                  />
                </Form.Item>
                <Form.Item name={[field.name, 'name']} className="mb-0 flex-1">
                  <Input placeholder="Shift name" disabled={readOnly} aria-label={`Shift ${String(index + 1)} name`} />
                </Form.Item>
                {overnight && <Tag className="mt-1.5">+1 day</Tag>}
                {fields.length > 1 && (
                  <Tooltip title={`Remove shift ${String(index + 1)}`}>
                    <Button
                      type="text"
                      htmlType="button"
                      className="mt-0.5"
                      disabled={readOnly}
                      icon={<Trash2 className="size-4" />}
                      aria-label={`Remove shift ${String(index + 1)}`}
                      onClick={() => {
                        remove(field.name);
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Form.List>

    {draft.shifts.some((shift) => shift.endTime !== '' && shift.endTime <= shift.startTime) && (
      <p className="text-xs text-muted-foreground" data-testid="overnight-explained">
        A shift whose end time is at or before its start runs into the next day. The duty still belongs to the day it
        started on.
      </p>
    )}

    {/* ⚠️ THE BAR GOES WHERE THE MISTAKE IS MADE, not in the right rail. */}
    <CoverageBar shifts={draft.shifts} hint={RTC_COPY.coverageBar} />
    <p className="text-xs text-muted-foreground">{RTC_COPY.coverageAdvisory}</p>
  </div>
);

export const RunsForSection = ({
  draft,
  readOnly,
  endsKind,
  horizonDays,
  fillOpenLeadHours,
}: SectionProps & {
  endsKind: StandingOrderEndsKind;
  horizonDays: number | null;
  fillOpenLeadHours: number | null;
}) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-wrap items-end gap-3">
      <Form.Item name="startDate" label="From" tooltip={RTC_COPY.startDate} className="mb-0">
        <DatePicker format="dd LLL yyyy" disabled={readOnly} data-testid="rtc-start-date" />
      </Form.Item>

      <Form.Item name="endsKind" label="Ends" className="mb-0">
        <Segmented
          disabled={readOnly}
          data-testid="rtc-ends-kind"
          options={[
            { value: 'NEVER', label: <Tooltip title={RTC_COPY.endsNever}>Until I end it</Tooltip> },
            { value: 'ON_DATE', label: <Tooltip title={RTC_COPY.endsOnDate}>On a date</Tooltip> },
          ]}
        />
      </Form.Item>

      {/* Registered either way — hidden with the CLASS, so the value survives a switch. */}
      <Form.Item name="endDate" label="Last day" className={endsKind === 'ON_DATE' ? 'mb-0' : 'mb-0 hidden'}>
        <DatePicker format="dd LLL yyyy" disabled={readOnly} />
      </Form.Item>
    </div>

    <hr className="border-border" />

    {/*
      ⚠️ STATED, NOT EDITABLE. Both timers are set for the whole system in the environment —
      the brief calls them env-configurable — so a field here would be a third source of
      truth that goes quietly wrong the day a deployment's configuration changes. The
      numbers come from the SERVER's own preview.
    */}
    <dl className="flex flex-wrap gap-x-8 gap-y-2 text-sm" data-testid="rtc-timers">
      <div className="flex flex-col">
        <dt className="text-xs text-muted-foreground">
          <Tooltip title={RTC_COPY.horizonDays}>Orders kept ready</Tooltip>
        </dt>
        <dd className="tabular-nums">{horizonDays === null ? '—' : `${String(horizonDays)} days ahead`}</dd>
      </div>
      <div className="flex flex-col">
        <dt className="text-xs text-muted-foreground">
          <Tooltip title={RTC_COPY.fillOpenLeadHours}>Divisions asked</Tooltip>
        </dt>
        <dd className="tabular-nums">
          {fillOpenLeadHours === null ? '—' : `${String(fillOpenLeadHours)} hours before each duty`}
        </dd>
      </div>
    </dl>
    <p className="text-xs text-muted-foreground">{RTC_COPY.timersAreSettings}</p>

    <span className="hidden">{draft.startDate}</span>
  </div>
);

/** ⚠️ WHY THERE IS NO SUPERVISION SECTION — answered on the form, not in a wiki. */
export const NoCommandNote = () => (
  <Alert
    type="info"
    showIcon
    data-testid="no-command-note"
    message={RTC_COPY.noSupervision}
    description={RTC_COPY.noSupervisionWhy}
  />
);
